import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/constants';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';
import '../styles/events.css';

const WorkshopLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const returnTo = searchParams.get('returnTo') || '/workshop';
  const eventIdFromUrl = searchParams.get('eid') ? parseInt(searchParams.get('eid'), 10) : null;

  const isRegisterPath = location.pathname === '/events/register';
  const showTabs = searchParams.get('atb') === '1';
  const [tab, setTab] = useState(isRegisterPath && showTabs ? 'register' : 'login'); // 'login' | 'register'
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '', mobile: '', event_id: '', eventtype: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  useEffect(() => {
    setEventsLoading(true);
    fetch(`${API_ENDPOINTS.BASE_URL}/events?upcoming=true&status=published&limit=50`, {
      headers: { [TOKEN_HEADER]: generateToken() },
    })
      .then(r => r.json())
      .then(d => { if (d.success) setUpcomingEvents(d.data || []); })
      .catch(() => {})
      .finally(() => setEventsLoading(false));
  }, []);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_ENDPOINTS.BASE_URL}/workshops/attendees/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        },
        body: JSON.stringify({ email: form.email, password: form.password, ...(eventIdFromUrl ? { eventid: eventIdFromUrl } : {}) }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const attendee = data.attendee || {};
        const attendeeId = String(attendee.id || data.attendeeid || '');
        const attendeeEmail = attendee.email || form.email || '';
        const attendeeName =
          attendee.name ||
          [attendee.first_name, attendee.last_name].filter(Boolean).join(' ') ||
          '';

        const attendeeProfile = {
          id: attendeeId,
          name: attendeeName,
          email: attendeeEmail,
          first_name: attendee.first_name || '',
          last_name: attendee.last_name || '',
          mobile: attendee.mobile || attendee.phone || '',
          ...attendee,
        };

        // Keep both attendee-specific and generic keys for compatibility across pages.
        localStorage.setItem('token', data.token || '');
        localStorage.setItem('attendeeToken', data.token || '');
        localStorage.setItem('attendeeId', attendeeId);
        localStorage.setItem('userId', attendeeId);
        localStorage.setItem('userid', attendeeId);
        localStorage.setItem('attendeeName', attendeeName);
        localStorage.setItem('userName', attendeeName);
        localStorage.setItem('attendeeEmail', attendeeEmail);
        localStorage.setItem('userEmail', attendeeEmail);
        localStorage.setItem('attendeeProfile', JSON.stringify(attendeeProfile));
        localStorage.setItem('userDetails', JSON.stringify(attendeeProfile));
        navigate(returnTo);
      } else {
        setError(data.error || data.message || 'Invalid email or password.');
      }
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const first_name = (form.first_name || '').trim();
      const last_name = (form.last_name || '').trim();
      const event_ids = form.event_id ? [parseInt(form.event_id, 10)] : [];
      const eventtype = form.eventtype !== '' ? parseInt(form.eventtype, 10) : undefined;
      const res = await fetch(`${API_ENDPOINTS.BASE_URL}/workshops/attendees/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        },
        body: JSON.stringify({ first_name, last_name, email: form.email, password: form.password, phone: form.mobile, event_ids, eventtype }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccessMsg('Account created! You can now sign in.');
        setTab('login');
        setForm({ ...form, first_name: '', last_name: '', password: '' });
      } else {
        setError(data.error || data.message || 'Registration failed. Please try again.');
      }
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ep-page ep-login-page">
      <div className="ep-hero-glow" aria-hidden="true" />

      <div className="ep-login-wrap">
        {/* Back link */}
        <Link to="/events" className="ep-login-back">← Back to Events</Link>

        <div className="ep-login-card">
          {/* Header */}
          <div className="ep-login-header">
            <span className="ep-live-badge">
              <span className="ep-pulse-dot" />
              Attendee Portal
            </span>
            <h1 className="ep-login-title">Your Workshop Hub</h1>
            <p className="ep-muted ep-login-sub">
              Manage your RSVPs, access event materials, and track your learning journey.
            </p>
          </div>

          {/* Tab switcher — only shown when ?atb=1 is present */}
          {showTabs && (
          <div className="ep-login-tabs">
            <button
              className={`ep-login-tab${tab === 'login' ? ' ep-login-tab-active' : ''}`}
              onClick={() => { setTab('login'); setError(''); setSuccessMsg(''); }}
            >
              Sign In
            </button>
            <button
              className={`ep-login-tab${tab === 'register' ? ' ep-login-tab-active' : ''}`}
              onClick={() => { setTab('register'); setError(''); setSuccessMsg(''); }}
            >
              Create Account
            </button>
          </div>
          )}

          {/* Success message */}
          {successMsg && (
            <div className="ep-login-success">{successMsg}</div>
          )}

          {/* Error message */}
          {error && (
            <div className="ep-login-error">{error}</div>
          )}

          {/* Login form — always shown */}
          <form className="ep-rsvp-form ep-login-form" onSubmit={handleLogin}>
              <div className="ep-form-group">
                <label htmlFor="wl-email">Email Address</label>
                <input
                  type="email"
                  id="wl-email"
                  value={form.email}
                  onChange={update('email')}
                  required
                  placeholder="you@email.com"
                  autoComplete="email"
                />
              </div>
              <div className="ep-form-group">
                <label htmlFor="wl-password">Password</label>
                <input
                  type="password"
                  id="wl-password"
                  value={form.password}
                  onChange={update('password')}
                  required
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>
              <button type="submit" className="ep-submit-btn" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign In →'}
              </button>
              <p className="ep-login-hint ep-muted">
                Forgot your password?{' '}
                <button type="button" className="ep-link-btn" onClick={() => alert('Password reset — coming soon.')}>
                  Reset it
                </button>
              </p>
            </form>

          {/* Register form */}
          {tab === 'register' && (
            <form className="ep-rsvp-form ep-login-form" onSubmit={handleRegister}>
              <div className="ep-form-group">
                <label htmlFor="wl-first-name">First Name</label>
                <input
                  type="text"
                  id="wl-first-name"
                  value={form.first_name}
                  onChange={update('first_name')}
                  required
                  placeholder="First name"
                  autoComplete="given-name"
                />
              </div>
              <div className="ep-form-group">
                <label htmlFor="wl-last-name">Last Name</label>
                <input
                  type="text"
                  id="wl-last-name"
                  value={form.last_name}
                  onChange={update('last_name')}
                  required
                  placeholder="Last name"
                  autoComplete="family-name"
                />
              </div>
              <div className="ep-form-group">
                <label htmlFor="wl-reg-email">Email Address</label>
                <input
                  type="email"
                  id="wl-reg-email"
                  value={form.email}
                  onChange={update('email')}
                  required
                  placeholder="you@email.com"
                  autoComplete="email"
                />
              </div>
              <div className="ep-form-group">
                <label htmlFor="wl-mobile">Mobile (optional)</label>
                <input
                  type="tel"
                  id="wl-mobile"
                  value={form.mobile}
                  onChange={update('mobile')}
                  placeholder="+91 98765 43210"
                  autoComplete="tel"
                />
              </div>
              <div className="ep-form-group">
                <label htmlFor="wl-event">Register for Event <span className="ep-muted">(optional)</span></label>
                <select
                  id="wl-event"
                  value={form.event_id}
                  onChange={update('event_id')}
                  className="ep-form-select"
                  disabled={eventsLoading}
                >
                  <option value="">{eventsLoading ? 'Loading events…' : '— Select an upcoming event —'}</option>
                  {upcomingEvents.map(ev => (
                    <option key={ev.id} value={ev.id}>
                      {ev.title}{ev.start_datetime ? ` · ${new Date(ev.start_datetime).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ep-form-group">
                <label htmlFor="wl-eventtype">Workshop Type <span className="ep-muted">(optional)</span></label>
                <select
                  id="wl-eventtype"
                  value={form.eventtype}
                  onChange={update('eventtype')}
                  className="ep-form-select"
                >
                  <option value="">— Select workshop type —</option>
                  <option value="0">Agentic Stack</option>
                  <option value="1">Aspiring Architect Program</option>
                  <option value="2">Career Authoring Program</option>
                </select>
              </div>
              <div className="ep-form-group">
                <label htmlFor="wl-reg-password">Create Password</label>
                <input
                  type="password"
                  id="wl-reg-password"
                  value={form.password}
                  onChange={update('password')}
                  required
                  minLength={8}
                  placeholder="Min 8 characters"
                  autoComplete="new-password"
                />
              </div>
              <button type="submit" className="ep-submit-btn" disabled={loading}>
                {loading ? 'Creating account…' : 'Create Account →'}
              </button>
            </form>
          )}

          {/* What you get section */}
          <div className="ep-login-perks">
            <p className="ep-login-perks-title">What you get</p>
            <div className="ep-login-perk-list">
              {[
                { icon: '📅', title: 'Manage RSVPs', desc: 'View and cancel your event registrations' },
                { icon: '📎', title: 'Event Materials', desc: 'Slides, code repos & resources post-event' },
                { icon: '🎓', title: 'Certificates', desc: 'Download attendance certificates (coming soon)' },
                { icon: '💬', title: 'Community', desc: 'Connect with the cohort before & after (coming soon)' },
              ].map(({ icon, title, desc }) => (
                <div key={title} className="ep-login-perk">
                  <span className="ep-login-perk-icon">{icon}</span>
                  <div>
                    <div className="ep-login-perk-title">{title}</div>
                    <div className="ep-muted ep-login-perk-desc">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkshopLogin;
